import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "../lib/auth";
import { getGameStats, getReferralStats, getInvestments, getLoans } from "../lib/api";
import { formatCurrency } from "../lib/utils";
import { Button } from "../components/ui/button";
import {
  Wallet,
  Dices,
  Gift,
  TrendingUp,
  ArrowRight,
  Users,
  Coins,
  PiggyBank,
  Landmark,
  CheckCircle,
  Clock,
  XCircle,
  Gamepad2,
  Trophy,
} from "lucide-react";

const statusIcons = {
  active: Clock,
  completed: CheckCircle,
  cancelled: XCircle,
  repaid: CheckCircle,
  liquidated: XCircle,
};

const statusColors = {
  active: "text-yellow-500",
  completed: "text-primary",
  cancelled: "text-destructive",
  repaid: "text-primary",
  liquidated: "text-destructive",
};

export default function Dashboard() {
  const { user } = useAuth();
  const [gameStats, setGameStats] = useState(null);
  const [referralStats, setReferralStats] = useState(null);
  const [investments, setInvestments] = useState({ active: [], closed: [], total_invested: 0 });
  const [loans, setLoans] = useState({ active: [], closed: [], total_borrowed: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      const [gamesRes, referralRes, investmentsRes, loansRes] = await Promise.all([
        getGameStats(),
        getReferralStats(),
        getInvestments(),
        getLoans(),
      ]);
      setGameStats(gamesRes.data);
      setReferralStats(referralRes.data);
      setInvestments(investmentsRes.data);
      setLoans(loansRes.data);
    } catch (error) {
      console.error("Failed to load stats:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen p-4 md:p-8">
      <div className="max-w-6xl mx-auto">
        {/* Welcome Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <h1 className="text-3xl font-heading font-bold mb-2">
            Welcome back, <span className="text-primary">{user?.username}</span>
          </h1>
          <p className="text-muted-foreground">
            Here's an overview of your PassivePay account
          </p>
        </motion.div>

        {/* Balance Cards */}
        <div className="grid md:grid-cols-2 gap-6 mb-8">
          {/* PAY Coin Balance - Primary Focus */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="glass-card rounded-2xl p-6 border-secondary/30"
          >
            <div className="flex items-center gap-3 mb-2">
              <div className="w-12 h-12 rounded-xl bg-secondary/20 flex items-center justify-center">
                <Coins className="w-6 h-6 text-secondary" />
              </div>
              <div>
                <span className="text-sm text-muted-foreground">PAY Coin Balance</span>
                <div className="text-3xl font-heading font-bold text-secondary" data-testid="pay-balance">
                  {(user?.pay_balance || 0).toLocaleString()} PAY
                </div>
              </div>
            </div>
            <div className="flex gap-2 mt-4">
              <Link to="/play" className="flex-1">
                <Button className="w-full bg-secondary hover:bg-secondary/90 font-heading">
                  <Gamepad2 className="w-4 h-4 mr-2" /> Play & Earn
                </Button>
              </Link>
              <Link to="/wallet">
                <Button variant="outline" className="border-secondary/50 text-secondary">
                  Withdraw
                </Button>
              </Link>
            </div>
          </motion.div>

          {/* USDT Balance */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
            className="glass-card rounded-2xl p-6"
          >
            <div className="flex items-center gap-3 mb-2">
              <div className="w-12 h-12 rounded-xl bg-primary/20 flex items-center justify-center">
                <Wallet className="w-6 h-6 text-primary" />
              </div>
              <div>
                <span className="text-sm text-muted-foreground">USDT Balance</span>
                <div className="text-3xl font-heading font-bold text-primary" data-testid="dashboard-balance">
                  {formatCurrency(user?.balance || 0)}
                </div>
              </div>
            </div>
            <Link to="/wallet">
              <Button size="sm" variant="outline" className="mt-4 border-primary/50 text-primary w-full">
                <Wallet className="w-4 h-4 mr-2" /> Manage Wallet
              </Button>
            </Link>
          </motion.div>
        </div>

        {/* Game Stats - Highlighted */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="glass-card rounded-2xl p-6 mb-8 border-secondary/20"
        >
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-secondary/20 flex items-center justify-center">
                <Trophy className="w-5 h-5 text-secondary" />
              </div>
              <div>
                <h3 className="font-heading font-bold">Game Stats</h3>
                <p className="text-xs text-muted-foreground">Your Lucky 7 performance</p>
              </div>
            </div>
            <Link to="/play">
              <Button size="sm" className="bg-secondary hover:bg-secondary/90">
                Play Now <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="p-4 rounded-xl bg-background/50 text-center">
              <Dices className="w-5 h-5 text-muted-foreground mx-auto mb-2" />
              <div className="text-2xl font-heading font-bold">{gameStats?.total_games || 0}</div>
              <div className="text-xs text-muted-foreground">Games Played</div>
            </div>
            <div className="p-4 rounded-xl bg-background/50 text-center">
              <TrendingUp className="w-5 h-5 text-secondary mx-auto mb-2" />
              <div className="text-2xl font-heading font-bold text-secondary">{gameStats?.wins || 0}</div>
              <div className="text-xs text-muted-foreground">Wins</div>
            </div>
            <div className="p-4 rounded-xl bg-background/50 text-center">
              <div className="w-5 h-5 mx-auto mb-2 text-muted-foreground font-bold">%</div>
              <div className="text-2xl font-heading font-bold">{gameStats?.win_rate?.toFixed(1) || 0}%</div>
              <div className="text-xs text-muted-foreground">Win Rate</div>
            </div>
            <div className="p-4 rounded-xl bg-secondary/10 border border-secondary/20 text-center">
              <Coins className="w-5 h-5 text-secondary mx-auto mb-2" />
              <div className="text-2xl font-heading font-bold text-secondary">{gameStats?.total_pay_earned || 0}</div>
              <div className="text-xs text-muted-foreground">PAY Earned</div>
            </div>
          </div>
        </motion.div>

        {/* Additional Stats */}
        <div className="grid md:grid-cols-2 gap-4 mb-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25 }}
            className="glass-card rounded-xl p-6"
          >
            <div className="flex items-center gap-3 mb-2">
              <Users className="w-5 h-5 text-accent" />
              <span className="text-sm text-muted-foreground">Referrals</span>
            </div>
            <div className="text-3xl font-heading font-bold">
              {referralStats?.total_referrals || 0}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="glass-card rounded-xl p-6"
          >
            <div className="flex items-center gap-3 mb-2">
              <Gift className="w-5 h-5 text-primary" />
              <span className="text-sm text-muted-foreground">Referral Earnings</span>
            </div>
            <div className="text-3xl font-heading font-bold text-primary">
              {formatCurrency(referralStats?.total_earnings || 0)}
            </div>
          </motion.div>
        </div>

        {/* Investments & Loans Section */}
        <div className="grid md:grid-cols-2 gap-6 mb-8">
          {/* Investments */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.35 }}
            className="glass-card rounded-2xl p-6"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-primary/20 flex items-center justify-center">
                  <PiggyBank className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <h3 className="font-heading font-bold">Investments</h3>
                  <p className="text-xs text-muted-foreground">Earn Category</p>
                </div>
              </div>
              <Link to="/earn">
                <Button size="sm" variant="ghost" className="text-primary">
                  Invest <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </Link>
            </div>

            <div className="space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Active Investments</span>
                <span className="font-bold text-primary">{investments.active?.length || 0}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Total Invested</span>
                <span className="font-bold">{formatCurrency(investments.total_invested || 0)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Closed</span>
                <span className="font-bold">{investments.closed?.length || 0}</span>
              </div>
            </div>
          </motion.div>

          {/* Loans */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="glass-card rounded-2xl p-6"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-accent/20 flex items-center justify-center">
                  <Landmark className="w-5 h-5 text-accent" />
                </div>
                <div>
                  <h3 className="font-heading font-bold">Crypto Loans</h3>
                  <p className="text-xs text-muted-foreground">Borrowed</p>
                </div>
              </div>
              <Link to="/crypto-loans">
                <Button size="sm" variant="ghost" className="text-accent">
                  Apply <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </Link>
            </div>

            <div className="space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Active Loans</span>
                <span className="font-bold text-accent">{loans.active?.length || 0}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Total Borrowed</span>
                <span className="font-bold">{formatCurrency(loans.total_borrowed || 0)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Closed</span>
                <span className="font-bold">{loans.closed?.length || 0}</span>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Referral Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.45 }}
          className="glass-card rounded-2xl p-6 hover:border-primary/30 transition-colors"
        >
          <div className="flex items-center gap-4 mb-4">
            <div className="w-12 h-12 rounded-xl bg-primary/20 flex items-center justify-center">
              <Gift className="w-6 h-6 text-primary" />
            </div>
            <div>
              <h3 className="font-heading font-bold text-lg">Referral Program</h3>
              <p className="text-sm text-muted-foreground">Invite friends & earn rewards</p>
            </div>
          </div>
          <p className="text-muted-foreground mb-4">
            Earn $10 for every friend you refer. Share your code and grow together!
          </p>
          <Link to="/referral">
            <Button variant="outline" className="w-full border-primary/50 text-primary hover:bg-primary/10">
              View Referrals <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </motion.div>
      </div>
    </div>
  );
}
